/* ==========================================================================
   Reality • Multi Columns (JS)
   - Reveal (IO, respecte prefers-reduced-motion)
   - Equal-height "safe": uniformise .mc-card__body par rangée
     * ignoré en mobile snap
     * hauteur bornée (min/max) pour éviter des cartes démesurées
   - Navigation mobile (flèches) pour le scroll-snap
   ========================================================================== */
(() => {
    const prefersReduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* ------------------------------- Utils -------------------------------- */

    function getNumberVar(el, name, fallback) {
        const v = getComputedStyle(el).getPropertyValue(name).trim();
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : fallback;
    }

    function debounce(fn, ms) {
        let to;
        return (...args) => {
            clearTimeout(to);
            to = setTimeout(() => fn(...args), ms);
        };
    }

    /* ------------------------------ Equalize ------------------------------ */

    function computeColumns(section) {
        if (window.innerWidth >= 990) return getNumberVar(section, '--mc-cols-lg', 3);
        if (window.innerWidth >= 750) return getNumberVar(section, '--mc-cols-md', 2);
        return 1;
    }

    function clearEqualize(section) {
        section.querySelectorAll('.mc-card__body').forEach(b => (b.style.minHeight = ''));
    }

    function equalize(section) {
        const rail = section.querySelector('.mc__rail');
        if (!rail) return;

        // Skip in mobile snap (row concept inexistant)
        const isSnapMobile = section.classList.contains('mc--snap') && window.innerWidth < 750;
        if (isSnapMobile) { clearEqualize(section); return; }

        const enable = rail.getAttribute('data-equalize') !== 'false';
        if (!enable) { clearEqualize(section); return; }

        const cols = computeColumns(section);
        const bodies = Array.from(rail.querySelectorAll('.mc-card__body'));
        clearEqualize(section);
        if (cols <= 1 || bodies.length === 0) return;

        // Limites pour une hauteur "normale"
        const minH = getNumberVar(section, '--mc-eq-min', 120);
        const capH = getNumberVar(section, '--mc-eq-cap', 280);

        // Equalize par tranches de 'cols'
        for (let i = 0; i < bodies.length; i += cols) {
            const row = bodies.slice(i, i + cols);
            // Mesure la hauteur naturelle
            const heights = row.map(b => b.getBoundingClientRect().height);
            const maxH = Math.max(...heights);
            // On borne pour ne pas gonfler les cartes = min(max, cap) et ≥ min
            const target = Math.max(minH, Math.min(capH, Math.round(maxH)));
            row.forEach(b => (b.style.minHeight = `${target}px`));
        }
    }

    /* -------------------------------- Reveal ------------------------------ */

    function reveal(section) {
        if (prefersReduce) return;
        if (section.dataset.reveal !== 'true') return;

        const offset = getNumberVar(section, '--mc-reveal-offset', 60);
        const obs = new IntersectionObserver(
            entries => {
                for (const e of entries) {
                    if (e.isIntersecting) {
                        e.target.classList.add('is-revealed');
                        obs.unobserve(e.target);
                    }
                }
            },
            { rootMargin: `0px 0px -${offset}px 0px`, threshold: 0.12 }
        );
        section.querySelectorAll('.mc-card').forEach(card => obs.observe(card));
    }

    /* --------------------------------- Nav -------------------------------- */

    function navAmount(rail) {
        const card = rail.querySelector('.mc-card');
        if (!card) return Math.round(rail.clientWidth * 0.9);
        const style = getComputedStyle(rail);
        const gap = parseFloat(style.gap || style.columnGap || '0') || 0;
        return Math.round(card.getBoundingClientRect().width + gap);
    }

    function navUpdateState(rail, prevBtn, nextBtn) {
        const atStart = rail.scrollLeft <= 2;
        const atEnd = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2;
        if (prevBtn) prevBtn.disabled = atStart;
        if (nextBtn) nextBtn.disabled = atEnd;
    }

    function initNav(section) {
        if (!section.classList.contains('mc--snap')) return;
        const rail = section.querySelector('.mc__rail');
        const prevBtn = section.querySelector('.mc__nav-btn--prev');
        const nextBtn = section.querySelector('.mc__nav-btn--next');
        const navWrap = section.querySelector('.mc__nav');
        if (!rail || !prevBtn || !nextBtn || !navWrap) return;

        const behavior = prefersReduce ? 'auto' : 'smooth';
        const go = dir => rail.scrollBy({ left: dir * navAmount(rail), behavior });

        prevBtn.addEventListener('click', () => go(-1));
        nextBtn.addEventListener('click', () => go(1));

        const onScrollOrResize = () => navUpdateState(rail, prevBtn, nextBtn);
        rail.addEventListener('scroll', onScrollOrResize, { passive: true });
        window.addEventListener('resize', onScrollOrResize);

        // Afficher les flèches uniquement en cas d’overflow réel (mobile)
        const toggleNavVisibility = () => {
            const mobile = window.innerWidth < 750;
            const hasOverflow = rail.scrollWidth > rail.clientWidth + 1;
            navWrap.style.display = (mobile && hasOverflow) ? 'block' : 'none';
            onScrollOrResize();
        };

        toggleNavVisibility();
        setTimeout(toggleNavVisibility, 80);
    }

    /* --------------------------------- Init ------------------------------- */

    function init(root = document) {
        root.querySelectorAll('.mc').forEach(section => {
            reveal(section);
            equalize(section);
            initNav(section);

            // Re-equalize sur resize (debounce)
            const onResize = debounce(() => {
                equalize(section);
            }, 120);
            window.addEventListener('resize', onResize);

            // Re-equalize après chargement des images
            section.querySelectorAll('img').forEach(img => {
                if (img.complete) return;
                img.addEventListener('load', () => equalize(section), { once: true });
            });
        });
    }

    document.addEventListener('DOMContentLoaded', () => init());

    // Theme Editor
    if (window.Shopify && Shopify.designMode) {
        document.addEventListener('shopify:section:load', e => init(e.target));
        document.addEventListener('shopify:section:reorder', () => init(document));
        document.addEventListener('shopify:block:select', e => {
            const s = e.target.closest('.mc');
            if (s) equalize(s);
        });
    }
})();
